import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../Glob';
import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { Constants } from '../../../../_common/platform/ts/Constants';

import { AppDispatcher as Dispatcher } from '../dispatcher/AppDispatcher';
import { Action, AppActions, I, check as checkAction } from '../actions/AppActions';

export interface IToolbarButtonsStoreState {
    cancelIsOn: boolean;
    applySwitchers: {
        [tabID: number]: boolean;
    };
    fullScreenModeIsOn: boolean;
    escapeIsLocked: boolean;
}

export class ToolbarButtonsStore extends BaseStore<IToolbarButtonsStoreState, AppActions, Dispatcher, IGlob> {
    public _state: IToolbarButtonsStoreState;
    public static _name = 'toolbar-buttons';

    public static getInitialState(): IToolbarButtonsStoreState {
        return {
            cancelIsOn: false,
            applySwitchers: {},
            fullScreenModeIsOn: false,
            escapeIsLocked: false
        };
    }

    constructor(glob: IGlob, dispatcher: Dispatcher, actions: AppActions) {
        super(glob, dispatcher, actions, ToolbarButtonsStore._name);

        this.readCache();

        document.addEventListener('fullscreenchange', this.handleOnFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.handleOnFullscreenChange);
        document.addEventListener('mozfullscreenchange', this.handleOnFullscreenChange);
        document.addEventListener('MSFullscreenChange', this.handleOnFullscreenChange);
    }

    private handleOnFullscreenChange = (e) => {
        this._state.fullScreenModeIsOn = e.currentTarget?.fullscreen;
        this.emitChange();

        if (this._state.escapeIsLocked && !this._state.fullScreenModeIsOn) {
            this.glob.AppActions.setLockEscape(false);
        }
    };

    public get fullScreenModeIsOn() {
        return this._state.fullScreenModeIsOn;
    }

    private setCancelOn() {
        if (this._state.cancelIsOn === true) {
            return false;
        }

        this._state.cancelIsOn = true;
        return true;
    }

    private setCancelOff() {
        if (this._state.cancelIsOn === false) {
            return false;
        }

        this._state.cancelIsOn = false;
        return true;
    }

    private switchApply(tabID: number, value: boolean) {
        if (value != null) {
            this._state.applySwitchers[tabID] = value;
        } else {
            delete this._state.applySwitchers[tabID];
        }
    }

    public serializeDataToStore(state: IToolbarButtonsStoreState): string {
        const stateCopy = $.extend(true, {}, state);

        delete stateCopy.cancelIsOn;

        if (_.isEmpty(stateCopy.applySwitchers)) {
            delete stateCopy.applySwitchers;
        }

        if (_.isEmpty(stateCopy)) {
            return '';
        }

        return JSON.stringify(stateCopy);
    }

    private toggleFullScreenMode() {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const elem: any = isSafari ? document.documentElement : document.body;

        if (!document.fullscreenElement) {
            let openFn =
                elem.requestFullscreen ||
                elem.mozRequestFullScreen ||
                elem.webkitRequestFullscreen ||
                elem.msRequestFullscreen;
            if (openFn) {
                openFn
                    .call(elem)
                    .then(function () {
                        // element has entered fullscreen mode successfully
                    })
                    .catch(function (error) {
                        // element could not enter fullscreen mode
                    });
            }
        } else {
            const doc: any = document;

            if (doc.exitFullscreen) {
                doc.exitFullscreen();
            }

            if (doc.webkitExitFullscreen) {
                /* Safari */
                doc.webkitExitFullscreen();
            }

            if (doc.msExitFullscreen) {
                /* IE11 */
                doc.msExitFullscreen();
            }
        }
    }

    private setLockEscape(value: boolean) {
        if (value) {
            if (this._state.fullScreenModeIsOn && (window.navigator as any)?.keyboard?.lock != null) {
                (window.navigator as any).keyboard.lock(['Escape']);
                this._state.escapeIsLocked = true;
            }
        } else if (this._state.escapeIsLocked) {
            setTimeout(() => {
                (window.navigator as any)?.keyboard?.unlock(['Escape']);
                this._state.escapeIsLocked = false;
            }, 500);
        }
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const a = payload.action;

        if (checkAction<I.TOOLBAR_BUTTONS_CANCEL_ON>(a, Constants.TOOLBAR_BUTTONS_CANCEL_ON)) {
            if (this.setCancelOn()) {
                this.emitChangeWithoutSaving();
            }
        } else if (checkAction<I.TOOLBAR_BUTTONS_CANCEL_OFF>(a, Constants.TOOLBAR_BUTTONS_CANCEL_OFF)) {
            if (this.setCancelOff()) {
                this.emitChangeWithoutSaving();
            }
        } else if (checkAction<I.TOOLBAR_BUTTONS_SWITCH_APPLY>(a, Constants.TOOLBAR_BUTTONS_SWITCH_APPLY)) {
            this.switchApply(a.tabID, a.value);
            this.emitChange();
        } else if (checkAction<I.FILTERS_ARE_ON_SHELF>(a, Constants.FILTERS_ARE_ON_SHELF)) {
            this.emitChange();
        } else if (checkAction<I.TOGGLE_FULL_SCREEN_MODE>(a, Constants.TOGGLE_FULL_SCREEN_MODE)) {
            this.toggleFullScreenMode();
        } else if (checkAction<I.SET_LOCK_ESCAPE>(a, Constants.SET_LOCK_ESCAPE)) {
            this.setLockEscape(a.value);
        }

        return true;
    }

    public static get(glob: IGlob, dispatcher: Dispatcher, actions: AppActions) {
        return new ToolbarButtonsStore(glob, dispatcher, actions);
    }
}
